import { useState, useCallback } from 'react';
import { Filter, Plus, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

export interface FilterFieldDef {
    key: string;
    label: string;
    type: 'text' | 'number' | 'date' | 'select';
    options?: { value: string; label: string }[];
}

export interface FilterCondition {
    field: string;
    operator: string;
    value: string;
    type: FilterFieldDef['type'];
}

const TEXT_OPERATORS = [
    { value: 'contains', label: 'Chứa' },
    { value: 'equals', label: 'Bằng' },
    { value: 'startsWith', label: 'Bắt đầu' },
];

const NUMBER_OPERATORS = [
    { value: 'eq', label: '=' },
    { value: 'gt', label: '>' },
    { value: 'gte', label: '≥' },
    { value: 'lt', label: '<' },
    { value: 'lte', label: '≤' },
];

const DATE_OPERATORS = [
    { value: 'eq', label: 'Ngày' },
    { value: 'gte', label: 'Từ ngày' },
    { value: 'lte', label: 'Đến ngày' },
];

const SELECT_OPERATORS = [
    { value: 'eq', label: 'Bằng' },
];

function getOperators(type: FilterFieldDef['type']) {
    switch (type) {
        case 'number': return NUMBER_OPERATORS;
        case 'date': return DATE_OPERATORS;
        case 'select': return SELECT_OPERATORS;
        default: return TEXT_OPERATORS;
    }
}

interface AdvancedFilterPanelProps {
    fields: FilterFieldDef[];
    conditions: FilterCondition[];
    onChange: (conditions: FilterCondition[]) => void;
    className?: string;
    /** If true (default), renders with its own toggle button. If false, renders inline. */
    standalone?: boolean;
}

/**
 * AdvancedFilterPanel — Multi-condition filtering panel.
 * - standalone=true (default): renders its own toggle button
 * - standalone=false: renders inline, parent controls visibility
 */
export function AdvancedFilterPanel({
    fields,
    conditions,
    onChange,
    className,
    standalone = true,
}: AdvancedFilterPanelProps) {
    const [expanded, setExpanded] = useState(false);

    const addCondition = useCallback(() => {
        const firstField = fields[0];
        if (!firstField) return;
        const ops = getOperators(firstField.type);
        onChange([
            ...conditions,
            { field: firstField.key, operator: ops[0].value, value: '', type: firstField.type },
        ]);
    }, [fields, conditions, onChange]);

    const removeCondition = useCallback((index: number) => {
        onChange(conditions.filter((_, i) => i !== index));
    }, [conditions, onChange]);

    const updateCondition = useCallback((index: number, updates: Partial<FilterCondition>) => {
        const newConditions = [...conditions];
        newConditions[index] = { ...newConditions[index], ...updates };
        if (updates.field) {
            const fieldDef = fields.find(f => f.key === updates.field);
            if (fieldDef) {
                const ops = getOperators(fieldDef.type);
                newConditions[index].type = fieldDef.type;
                newConditions[index].operator = ops[0].value;
                newConditions[index].value = '';
            }
        }
        onChange(newConditions);
    }, [conditions, fields, onChange]);

    const clearAll = useCallback(() => {
        onChange([]);
    }, [onChange]);

    const activeCount = conditions.filter(c => c.value).length;
    const showPanel = standalone ? expanded : true;

    const conditionRows = (
        <div className="space-y-1.5 mb-2">
            {conditions.map((condition, index) => {
                const fieldDef = fields.find(f => f.key === condition.field);
                const operators = getOperators(condition.type);
                return (
                    <div key={index} className="flex items-center gap-1.5">
                        <Select value={condition.field} onValueChange={(v) => updateCondition(index, { field: v })}>
                            <SelectTrigger className="h-7 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {fields.map(f => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={condition.operator} onValueChange={(v) => updateCondition(index, { operator: v })}>
                            <SelectTrigger className="h-7 w-[80px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {operators.map(op => <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        {condition.type === 'select' && fieldDef?.options ? (
                            <Select value={condition.value} onValueChange={(v) => updateCondition(index, { value: v })}>
                                <SelectTrigger className="h-7 flex-1 text-xs"><SelectValue placeholder="Chọn..." /></SelectTrigger>
                                <SelectContent>
                                    {fieldDef.options.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Input
                                className="h-7 flex-1 text-xs"
                                type={condition.type === 'date' ? 'date' : condition.type === 'number' ? 'number' : 'text'}
                                value={condition.value}
                                onChange={(e) => updateCondition(index, { value: e.target.value })}
                                placeholder="Giá trị..."
                            />
                        )}
                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                            onClick={() => removeCondition(index)} type="button">
                            <Trash2 className="h-3 w-3" />
                        </Button>
                    </div>
                );
            })}
        </div>
    );

    const panelContent = (
        <div className={cn(
            'bg-white rounded-lg border shadow-sm p-2',
            standalone && 'absolute z-40 top-full mt-1 left-0 w-[520px] max-w-[90vw] shadow-lg p-3 animate-in fade-in-0 slide-in-from-top-1',
            !standalone && className
        )}>
            <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Bộ lọc nâng cao</span>
                {conditions.length > 0 && (
                    <Button variant="ghost" size="sm" className="h-5 text-[10px] text-muted-foreground px-1.5" onClick={clearAll} type="button">
                        <X className="h-2.5 w-2.5 mr-0.5" /> Xoá
                    </Button>
                )}
            </div>
            {conditionRows}
            <Button variant="outline" size="sm" className="h-6 text-[11px] w-full" onClick={addCondition} type="button">
                <Plus className="h-3 w-3 mr-1" /> Thêm điều kiện
            </Button>
        </div>
    );

    // Standalone: render toggle button + dropdown panel
    if (standalone) {
        return (
            <div className="relative">
                <Button
                    variant="outline"
                    size="sm"
                    className={cn('h-7 text-xs gap-1', activeCount > 0 && 'border-primary text-primary')}
                    onClick={() => setExpanded(!expanded)}
                    type="button"
                >
                    <Filter className="h-3 w-3" />
                    Lọc
                    {activeCount > 0 && (
                        <span className="ml-0.5 bg-primary text-primary-foreground rounded-full h-3.5 w-3.5 text-[9px] flex items-center justify-center">
                            {activeCount}
                        </span>
                    )}
                </Button>
                {showPanel && panelContent}
            </div>
        );
    }

    // Inline: render panel directly (parent controls visibility)
    return panelContent;
}
